import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch } from '../store';

interface AudioState {
    isPlaying: boolean;
    audioLoading: boolean;
    audioIndex: number;
    currentAudio: string | null;
    currentLang: string;
    audioInstance: HTMLAudioElement | null;
    isAPILoading: boolean;
}

const initialState: AudioState = {
    isPlaying: false,
    audioLoading: false,
    audioIndex: -1,
    currentAudio: null,
    currentLang: 'en',
    audioInstance: null,
    isAPILoading: false,
};

// Async thunk for playing audio
export const playAudioAsync = createAsyncThunk(
    'audio/playAudioAsync',
    async ({
        text,
        lang,
        getAudioUri,
        dispatch
    }: {
        text: string;
        lang: string;
        getAudioUri?: (text: string, lang: string) => Promise<string>;
        dispatch: AppDispatch;
    },
        { rejectWithValue }) => {
        try {
            if (lang === 'en') {
                const synth = window.speechSynthesis;
                const utterance = new SpeechSynthesisUtterance(text);

                return new Promise<void>((resolve, reject) => {
                    utterance.onend = () => resolve();
                    utterance.onerror = (error) => reject(error);
                    synth?.speak(utterance);
                });
            } else {
                if (!getAudioUri) {
                    throw new Error('getAudioUri function is required for non-English audio');
                }

                dispatch(setAPILoading(true));
                const cleanText = text.replace(/\([^()]*\)/g, ' ');
                const audioUrl = await getAudioUri(cleanText, lang);
                dispatch(setAPILoading(false));

                const audio = new Audio(audioUrl);

                // Store audio instance in Redux state
                dispatch(setAudioInstance(audio));

                return new Promise<void>((resolve, reject) => {
                    audio.onended = () => {
                        dispatch(setAudioInstance(null));
                        resolve();
                    };
                    audio.onerror = () => {
                        dispatch(setAudioInstance(null));
                        reject(new Error('Audio playback failed'));
                    };
                    audio.play().catch(error => {
                        dispatch(setAudioInstance(null));
                        reject(error);
                    });
                });
            }
        } catch (error) {
            return rejectWithValue(error);
        } finally {
            dispatch(setAPILoading(false));
        }
    }
);

const audioSlice = createSlice({
    name: 'audio',
    initialState,
    reducers: {
        stopAudio: (state) => {
            // Stop any playing audio
            if (state.audioInstance) {
                state.audioInstance.pause();
                state.audioInstance.currentTime = 0;
            }

            const synth = window.speechSynthesis;
            synth?.cancel();

            // Reset state
            state.isPlaying = false;
            state.audioIndex = -1;
            state.audioLoading = false;
            state.audioInstance = null;
        },
        setAudioIndex: (state, action: PayloadAction<number>) => {
            state.audioIndex = action.payload;
        },
        setCurrentLang: (state, action: PayloadAction<string>) => {
            state.currentLang = action.payload;
        },
        setAudioInstance: (state, action: PayloadAction<any>) => {
            state.audioInstance = action.payload;
        },
        setAPILoading: (state, action: PayloadAction<boolean>) => {
            state.isAPILoading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(playAudioAsync.pending, (state) => {
                state.audioLoading = true;
                state.isPlaying = true;
            })
            .addCase(playAudioAsync.fulfilled, (state) => {
                state.audioLoading = false;
                state.isPlaying = false;
                state.audioIndex = -1;
                state.audioInstance = null;
            })
            .addCase(playAudioAsync.rejected, (state) => {
                state.audioLoading = false;
                state.isPlaying = false;
                state.audioIndex = -1;
                state.audioInstance = null;
            });
    }
});

export const {
    stopAudio,
    setAudioIndex,
    setCurrentLang,
    setAudioInstance,
    setAPILoading,
} = audioSlice.actions;

export default audioSlice.reducer;